<template>
    <div class="step-schedule">
        <v-row>
            <v-col cols="12" md="6">
                <v-select 
                    :items="courseDurationItems" 
                    :value="classDuration" 
                    @input="$emit('update:classDuration', $event)"
                    outlined 
                    label="Class Duration" 
                    placeholder="Choose Class Duration" 
                    hide-details
                />
            </v-col>
            <v-col cols="12" md="6">
                <v-select 
                    :items="classesPerWeekItems" 
                    :value="classesPerWeek" 
                    @input="$emit('update:classesPerWeek', $event)"
                    :disabled="!classDuration" 
                    outlined 
                    label="Classes / Week" 
                    placeholder="How Many Classes Weekly" 
                    hide-details
                />
            </v-col>
            <v-col cols="12" md="6">
                <v-select 
                    :value="preferredDays" 
                    @input="$emit('update:preferredDays', $event)"
                    :items="preferredDaysItems" 
                    multiple 
                    outlined 
                    label="Preferred Days" 
                    placeholder="Preferred Days For Classes" 
                    hide-details
                />
            </v-col>
            <v-col cols="12" md="6">
                <v-menu 
                    v-model="startDateMenu" 
                    :close-on-content-click="false" 
                    :nudge-right="40" 
                    transition="scale-transition" 
                    offset-y 
                    min-width="auto"
                >
                    <template v-slot:activator="{ on, attrs }">
                        <v-text-field 
                            :disabled="!preferredDays.length" 
                            outlined 
                            :value="startDate" 
                            label="Start Date" 
                            placeholder="Start date" 
                            prepend-inner-icon="mdi-calendar" 
                            readonly 
                            hide-details 
                            v-bind="attrs" 
                            v-on="on"
                        />
                    </template>
                    <v-date-picker 
                        :allowed-dates="allowedDates" 
                        :min="minStartDate" 
                        :value="startDate" 
                        @input="onStartDateChange" 
                        color="primary"
                    />
                </v-menu>
            </v-col>
        </v-row>
        
        <div class="schedule-section mt-4">
            <div class="schedule-header">
                <v-icon size="20" class="me-2">mdi-clock-outline</v-icon>
                <span>Schedule</span>
            </div>
            <v-row v-for="(entry, i) in timeEntries" :key="'time-entry-' + i" class="schedule-row">
                <v-col cols="5">
                    <v-select 
                        :dense="mobile" 
                        :disabled="!preferredDays.length" 
                        :value="entry.day" 
                        @input="updateTimeEntry(i, 'day', $event)"
                        :items="selectedPreferredDays" 
                        outlined 
                        label="Day" 
                        placeholder="Day" 
                        hide-details
                    />
                </v-col>
                <v-col cols="5">
                    <time-input 
                        :value="entry.from" 
                        @input="updateTimeEntry(i, 'from', $event)"
                        :dense="mobile" 
                        :hide-icon="mobile" 
                        half-format 
                        :disabled="(!entry.day && entry.day !== 0)" 
                        outlined 
                        label="Time" 
                        placeholder="Time" 
                    />
                </v-col>
                <v-col cols="2" class="d-flex align-center justify-center">
                    <v-btn v-if="i > 0" color="error" icon small @click="removeTimeEntry(i)">
                        <v-icon size="18">mdi-close</v-icon>
                    </v-btn>
                </v-col>
            </v-row>
            <v-btn color="primary" text small class="mt-2" @click="addTimeEntry">
                <v-icon left small>mdi-plus</v-icon>
                Add Time Slot
            </v-btn>
        </div>
    </div>
</template>

<script>
import TimeInput from '@/components/custom/time-input';

export default {
    name: 'WizardStepSchedule',
    components: {
        TimeInput
    },
    props: {
        classDuration: {
            type: [String, Number],
            default: ''
        },
        classesPerWeek: {
            type: [String, Number],
            default: ''
        },
        startDate: {
            type: String,
            default: ''
        },
        timeEntries: {
            type: Array,
            default: () => []
        },
        preferredDays: {
            type: Array,
            default: () => []
        },
        courseDurationItems: {
            type: Array,
            default: () => []
        },
        classesPerWeekItems: {
            type: Array,
            default: () => []
        },
        minStartDate: {
            type: String,
            required: true
        },
        mobile: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            startDateMenu: false,
            preferredDaysItems: [
                { text: 'Saturday', value: 6 },
                { text: 'Sunday', value: 0 },
                { text: 'Monday', value: 1 },
                { text: 'Tuesday', value: 2 },
                { text: 'Wednesday', value: 3 },
                { text: 'Thursday', value: 4 },
                { text: 'Friday', value: 5 },
            ]
        };
    },
    computed: {
        selectedPreferredDays() {
            return this.preferredDaysItems.filter(day => {
                return this.preferredDays.includes(day.value);
            });
        }
    },
    methods: {
        onStartDateChange(date) {
            this.$emit('update:startDate', date);
            this.startDateMenu = false;
        },
        allowedDates(day) {
            const d = new Date(day);
            return this.preferredDays.includes(d.getUTCDay());
        },
        updateTimeEntry(index, field, value) {
            const updatedEntries = [...this.timeEntries];
            updatedEntries[index] = { ...updatedEntries[index], [field]: value };
            this.$emit('update:timeEntries', updatedEntries);
        },
        addTimeEntry() {
            const updatedEntries = [...this.timeEntries, { day: '', from: '', to: '' }];
            this.$emit('update:timeEntries', updatedEntries);
        },
        removeTimeEntry(index) {
            const updatedEntries = this.timeEntries.filter((_, i) => i !== index);
            this.$emit('update:timeEntries', updatedEntries);
        }
    }
};
</script>

<style lang="scss" scoped>
.schedule-section {
    background: #FAFAFA;
    border-radius: 8px;
    padding: 16px;
}

.schedule-header {
    display: flex;
    align-items: center;
    font-family: 'Inter', sans-serif;
    font-weight: 600;
    font-size: 16px;
    color: #1E1E1E;
    margin-bottom: 16px;
}

.schedule-row {
    margin-bottom: 8px;
}
</style>

